import { useCallback, useEffect, useState } from 'react';
import { HeapMaskPreferences } from './heapMasks';

const STORAGE_KEY = 'heap.maskPreferences.v1';

export interface UseHeapMaskPreferencesResult {
  preferences: HeapMaskPreferences;
  setMaskVisibility: (maskId: string, visible: boolean) => void;
  setMaskOpacity: (maskId: string, opacity: number) => void;
}

function readPreferences(): HeapMaskPreferences {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? (JSON.parse(stored) as Partial<HeapMaskPreferences>) : {};
    return { visibility: parsed.visibility ?? {}, opacity: parsed.opacity ?? {} };
  } catch {
    return { visibility: {}, opacity: {} };
  }
}

export function useHeapMaskPreferences(): UseHeapMaskPreferencesResult {
  const [preferences, setPreferences] = useState<HeapMaskPreferences>(readPreferences);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch {
      // Keep in-memory preferences when storage is unavailable.
    }
  }, [preferences]);

  const setMaskVisibility = useCallback((maskId: string, visible: boolean) => {
    setPreferences((current) => ({ ...current, visibility: { ...current.visibility, [maskId]: visible } }));
  }, []);

  const setMaskOpacity = useCallback((maskId: string, opacity: number) => {
    setPreferences((current) => ({ ...current, opacity: { ...current.opacity, [maskId]: opacity } }));
  }, []);

  return { preferences, setMaskVisibility, setMaskOpacity };
}
